import { createReducer, on } from '@ngrx/store';
import { Update } from '@ngrx/entity';

import { Counters, DashCounters } from '@apps/auth/domain/models/back/dash-counters';
import { BackAuthCountersLoaded } from '@apps/back/store/auth/auth.actions';
import { User } from '@apps/auth/domain/models/back';
import { BackConstants, ExternalStatus } from '@apps/back/back.constants';

import { IUsersState, updateUserActiveStatus, usersAdapter, usersInitialState } from '../users.common';
import { ODataAuth } from '../../ODataAuth';
import {
    BackAuthUsersExternalUserActiveStatusUpdated,
    BackAuthUsersExternalIdentityUpdated,
    BackAuthUsersExternalODataFailed,
    BackAuthUsersExternalODataLoaded,
    BackAuthUsersExternalODataRequested,
    BackAuthUsersExternalQsWordSet,
    BackAuthUsersExternalUserUpdated,
    BackAuthUsersExternalAddQueryParameterRequested,
    BackAuthUsersExternalRemoveQueryParameterRequested
} from './users-external.actions';
import { StatusesMetadata } from '@common/models';
import { BackAuthUsersRelocateUserSucceeded } from '../global/users-global.actions';

export const BackAuthUsersExternalFeatureKey = 'external';

export interface IBackAuthUsersExternalState extends StatusesMetadata
{
    counters: Counters;
    oData: ODataAuth;
    users: IUsersState;
    qsWord: string;
}

export const BackAuthUsersExternalInitialState: IBackAuthUsersExternalState =
{
    counters: new Counters(),
    oData: new ODataAuth(BackConstants.QSUsersTop, ExternalStatus),
    users: usersInitialState,
    qsWord: '',
    ...StatusesMetadata.init()
};

export const BackAuthUsersExternalReducer = createReducer(
    BackAuthUsersExternalInitialState,
    on(
        BackAuthCountersLoaded,
        (state: IBackAuthUsersExternalState, { usersCounters }: { entitiesCounters: DashCounters, usersCounters: DashCounters }) => ({
            ...state,
            counters: usersCounters.external
        })
    ),
    on(
        BackAuthUsersExternalODataRequested,
        (state: IBackAuthUsersExternalState) => ({
            ...state,
            ...StatusesMetadata.requested()
        })
    ),
    on(
        BackAuthUsersExternalODataLoaded,
        (state: IBackAuthUsersExternalState, { users }: {users: User[]}) => ({
            ...state,
            users: usersAdapter.setAll(users, state.users),
            ...StatusesMetadata.succeeded(users && users.length !== 0, users && users.length === 0)
        })
    ),
    on(
        BackAuthUsersExternalODataFailed,
        (state: IBackAuthUsersExternalState) => ({
            ...state,
            ...StatusesMetadata.failed()
        })
    ),
    on(
        BackAuthUsersExternalIdentityUpdated,
        (state: IBackAuthUsersExternalState, { user }: {user: Update<User>}) => ({
            ...state,
            users: usersAdapter.updateOne(user, state.users),
        })
    ),
    on(
        BackAuthUsersExternalQsWordSet,
        (state, { qsWord }: { qsWord: string; }) =>
            ({
                ...state,
                qsWord: qsWord.toLowerCase()
            })
    ),
    on(
        BackAuthUsersExternalUserActiveStatusUpdated,
        (state: IBackAuthUsersExternalState, { userId, active }: { userId: string; active: boolean }) => ({
            ...state,
            users: updateUserActiveStatus(state.users, userId, active)
        })
    ),
    on(
        BackAuthUsersExternalUserUpdated,
        (state: IBackAuthUsersExternalState, user) =>
        {
            const updateUser: Update<User> = {
                id: user.id,
                changes:{
                    ...user
                }
            };

            return {
                ...state,
                users: usersAdapter.updateOne(updateUser, state.users)
            };
        }
    ),
    on(
        BackAuthUsersExternalAddQueryParameterRequested,
        (state: IBackAuthUsersExternalState, queryParameter) =>
        {
            const oDataAuth: ODataAuth = state.oData;
            oDataAuth.addQueryParameter(queryParameter.key, queryParameter.value)

            return {
                ...state,
                oData:  oDataAuth
            };
        }
    ),
    on(
        BackAuthUsersExternalRemoveQueryParameterRequested,
        (state: IBackAuthUsersExternalState, param) =>
        {
            const oDataAuth: ODataAuth = state.oData;
            oDataAuth.removeQueryParameter(param.key)

            return {
                ...state,
                oData:  oDataAuth
            };
        }
    )
);
